import React, { useState, useMemo } from 'react';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Parses encounter summary data into the structure expected by the dashboard.
 * Supports two formats:
 *   1. Backend pre-parsed: data.parsed.{demographics, chief_complaint, hpi, assessment_plan, ...}
 *   2. Legacy Athena raw:  data.{Patient, Vitals, HPI_Templated, AssessmentPlan, ...}
 */
export const parseAthenaSummary = (data) => {
  const empty = { patient: { name: 'Unknown', initials: '??', id: '', dob: '', age: '', gender: '', appt: '' }, vitals: {}, cc: [], hpiLines: [], dx: [], meds: [], allergies: [], problems: [], social: {}, family: {}, surgical: [], followup: {} };
  if (!data) return empty;

  // ── Path 1: Use backend pre-parsed data if available ──
  const p = data.parsed;
  if (p) {
    const demo = p.demographics || {};
    const patient = {
      name: demo.name || 'Unknown Patient',
      initials: demo.name ? demo.name.split(',').reverse().map(n => (n.trim()[0] || '')).join('').toUpperCase() : '??',
      id: demo.id || demo.patient_id || '',
      dob: demo.dob || '',
      age: demo.age || '',
      gender: demo.gender || demo.sex || '',
      appt: demo.appointment || demo.encounter_date || ''
    };

    // Vitals — could be a string or already parsed
    const vitalsRaw = typeof p.vitals === 'string' ? p.vitals : '';
    const vitals = {
      bp: vitalsRaw.match(/BP[:\s]*([\d/]+)/)?.[1] || '--/--',
      hr: vitalsRaw.match(/(?:HR|Pulse)[:\s]*(\d+)/)?.[1] || '--',
      ht: vitalsRaw.match(/(?:Ht|Height)[:\s]*([\d'"′″.]+)/)?.[1] || '--',
      wt: vitalsRaw.match(/(?:Wt|Weight)[:\s]*([\d.]+\s*(?:lbs?|kg)?)/)?.[1] || '--',
      bmi: vitalsRaw.match(/BMI[:\s]*([\d.]+)/)?.[1] || '--',
    };

    // Chief complaint
    const cc = typeof p.chief_complaint === 'string'
      ? p.chief_complaint.split(/[,;\n]+/).map(s => s.trim()).filter(Boolean)
      : [];

    // HPI
    let hpiLines = [];
    if (Array.isArray(p.hpi)) {
      hpiLines = p.hpi.map(item => {
        if (typeof item === 'object') {
          return { label: item.condition || item.heading || item.label || 'Detail', text: (item.details || []).join('. ') || item.content || item.text || '' };
        }
        return { label: 'Detail', text: String(item) };
      }).filter(l => l.text);
    } else if (typeof p.hpi === 'string' && p.hpi.trim()) {
      hpiLines = p.hpi.split('\n').map(line => {
        const match = line.match(/^([^:]+):\s*(.*)$/);
        if (match) return { label: match[1].trim(), text: match[2].trim() };
        return { label: 'Detail', text: line.trim() };
      }).filter(l => l.text);
    }

    // Assessment & Plan
    const ap = p.assessment_plan || {};
    const dx = (ap.diagnoses || []).map((d, i) => {
      const nl = (d.narrative || '').toLowerCase();
      let status = 'active';
      if (nl.includes('stable') || nl.includes('well controlled') || nl.includes('well-controlled')) status = 'stable';
      else if (nl.includes('improv') || nl.includes('better') || nl.includes('resolved')) status = 'improving';
      else if (nl.includes('declin') || nl.includes('worsen')) status = 'declined';
      return {
        n: d.number || i + 1,
        title: d.title || 'Unknown Diagnosis',
        icd: (d.icd_code || '').split(':')[0]?.trim() || '',
        status,
        plan: d.orders?.length ? d.orders : (d.narrative || '').split('\n').slice(1).map(s => s.trim()).filter(Boolean)
      };
    });

    const meds = (p.medications || []).filter(m => m.name && m.name !== 'Name').map(m => ({
      name: m.name || '', sig: m.details || '', date: m.date || '', src: m.source || ''
    }));

    const allergies = (p.allergies || []).map(a => ({
      allergen: a.allergen || '', reaction: a.reaction || ''
    }));

    const problems = (p.problems || []).filter(Boolean);

    const social = {
      tobacco: p.social_history?.tobacco || 'Unknown',
      alcohol: p.social_history?.alcohol || 'Unknown',
      exercise: p.social_history?.exercise || 'Unknown'
    };

    const family = Object.entries(p.family_history || {}).reduce((acc, [rel, cond]) => {
      acc[rel] = Array.isArray(cond) ? cond.join(', ') : (typeof cond === 'string' ? cond : '');
      return acc;
    }, {});

    const surgical = (p.surgical_history || []).filter(Boolean);

    const followup = {
      instructions: (ap.instructions || []).join('. ') || 'None provided.',
      returnAppt: ap.return_appointment || 'Not specified'
    };

    // Freetext assessment (used when no structured diagnoses exist)
    const apText = ap.assessment_plan_text || '';

    return { patient, vitals, cc, hpiLines, dx, apText, meds, allergies, problems, social, family, surgical, followup };
  }

  // ── Path 2: Legacy fallback (raw Athena structure) ──
  const summary = data || {};
  
  const patient = {
    name: summary.Patient?.name || 'Unknown Patient',
    initials: summary.Patient?.name ? 
      summary.Patient.name.split(',').reverse().map(n => n.trim()[0]).join('').toUpperCase() : '??',
    id: summary.Patient?.id || '',
    dob: summary.Patient?.dob || '',
    age: summary.Patient?.age || '',
    gender: summary.Patient?.gender || '',
    appt: summary.Patient?.appointment || ''
  };

  const vitalsRaw = summary.Vitals || '';
  const vitals = {
    bp: vitalsRaw.match(/BP[:\s]*([\d/]+)/)?.[1] || '--/--',
    hr: vitalsRaw.match(/(?:HR|Pulse)[:\s]*(\d+)/)?.[1] || '--',
    ht: vitalsRaw.match(/(?:Ht|Height)[:\s]*([\d'"]+)/)?.[1] || '--',
    wt: vitalsRaw.match(/(?:Wt|Weight)[:\s]*([\d.\s\w]+)/)?.[1] || '--',
    bmi: vitalsRaw.match(/BMI[:\s]*([\d.]+)/)?.[1] || '--',
  };

  const cc = (summary.EncounterReason || '').split(/[,;\n]+/).map(s => s.trim()).filter(Boolean);

  const hpiRaw = summary.HPI_Templated || '';
  const hpiLines = hpiRaw.split('\n')
    .map(line => {
      const match = line.match(/^([^:]+):\s*(.*)$/);
      if (match) return { label: match[1].trim(), text: match[2].trim() };
      return { label: 'Detail', text: line.trim() };
    })
    .filter(line => line.text);

  const ap = summary.AssessmentPlan || {};
  const dx = (ap.diagnoses || []).map((d, i) => ({
    n: d.number || i + 1,
    title: d.title || 'Unknown Diagnosis',
    icd: d.icd_code?.split(':')[0]?.trim() || '',
    status: d.narrative?.toLowerCase().includes('stable') ? 'stable' : 
            d.narrative?.toLowerCase().includes('active') ? 'active' : 'stable',
    plan: d.orders || d.narrative?.split('\n').slice(1).map(s => s.trim()).filter(Boolean) || []
  }));

  const meds = (summary.medications || []).map(m => ({
    name: m.name || '', sig: m.details || '', date: m.date || '', src: m.source || ''
  }));

  const allergies = (summary.allergies || []).map(a => ({
    allergen: a.allergen || '', reaction: a.reaction || ''
  }));

  const problems = (summary.problems || []).filter(Boolean);

  const social = {
    tobacco: summary.social_history?.tobacco || 'Unknown',
    alcohol: summary.social_history?.alcohol || 'Unknown',
    exercise: summary.social_history?.exercise || 'Unknown'
  };

  const family = Object.entries(summary.family_history || {}).reduce((acc, [rel, cond]) => {
    acc[rel] = Array.isArray(cond) ? cond.join(', ') : cond;
    return acc;
  }, {});

  const surgical = (summary.surgical_history || []).filter(Boolean);

  const followup = {
    instructions: (ap.instructions || []).join('. ') || 'None provided.',
    returnAppt: ap.return_appointment || 'Not specified'
  };

  return { patient, vitals, cc, hpiLines, dx, meds, allergies, problems, social, family, surgical, followup };
};

const DxCard = ({ item }) => {
  return (
    <div className="py-1.5 flex items-start gap-2">
      <span className="text-[#c4bdb3] mt-[3px] shrink-0">•</span>
      <div className="flex-1 min-w-0">
        <span className="text-[13px] font-normal text-[#3a342c] leading-snug">{item.title}</span>
        {item.icd && (
          <span className="ml-2 text-[9px] font-mono bg-[#f0ede8] text-[#948b7f] px-1.5 py-0.5 rounded-full inline-block align-middle">
            {item.icd}
          </span>
        )}
        {item.plan && item.plan.length > 0 && (
          <div className="mt-1 text-[11px] text-[#948b7f] leading-snug">
            {item.plan.map((p, i) => <div key={i}>→ {p}</div>)}
          </div>
        )}
      </div>
    </div>
  );
};

const ProblemItem = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const needsTruncate = text.length > 40;
  return (
    <div className="py-1 flex items-center gap-1.5 text-[12px] text-[#3a342c]">
      <span className="text-[#c4bdb3] shrink-0">•</span>
      <span className={expanded ? '' : 'truncate'} style={expanded ? {} : { maxWidth: '100%' }}>
        {text}
      </span>
      {needsTruncate && !expanded && (
        <button onClick={() => setExpanded(true)} className="text-[#e8653a] text-[11px] shrink-0 hover:underline">…</button>
      )}
    </div>
  );
};

const RefTabs = ({ data }) => {
  const [activeTab, setActiveTab] = useState("Meds");
  const row1 = ["Meds", "Allergies", "Problems"];
  const row2 = ["Social", "Family", "Surgical"];

  const TabButton = ({ t }) => (
    <button
      onClick={() => setActiveTab(t)}
      className={`flex-1 py-2 text-[11px] font-medium transition-colors text-center
        ${activeTab === t ? 'text-[#e8653a] border-b-2 border-[#e8653a]' : 'text-[#948b7f] hover:text-[#5c5549]'}`}
    >
      {t}
    </button>
  );

  return (
    <div className="h-full flex flex-col bg-white border border-[#e4ded5] rounded-xl shadow-sm overflow-hidden">
      {/* Row 1 tabs */}
      <div className="flex border-b border-[#e4ded5] bg-[#faf8f5] shrink-0">
        {row1.map(t => <TabButton key={t} t={t} />)}
      </div>
      {/* Row 2 tabs */}
      <div className="flex border-b border-[#e4ded5] bg-[#faf8f5] shrink-0">
        {row2.map(t => <TabButton key={t} t={t} />)}
      </div>

      <div className="flex-1 overflow-auto p-3 custom-scrollbar" style={{ minHeight: 0 }}>
        {/* ── Meds: compact rows ── */}
        {activeTab === "Meds" && (
          <div>
            <div className="text-[9px] text-[#b0a89c] italic mb-2 text-center">Current medication list</div>
            {(data.meds || []).length > 0 ? data.meds.map((m, i) => (
              <div key={i} className="py-2 border-b border-[#f5f3ef] last:border-0">
                <div className="font-semibold text-[12px] text-[#1a1613] leading-tight">{m.name}</div>
                {m.sig && <div className="text-[11px] text-[#948b7f] mt-0.5 leading-snug">{m.sig}</div>}
                {m.date && (
                  <span className="inline-block mt-1 text-[9px] font-mono bg-[#f0ede8] text-[#948b7f] px-1.5 py-0.5 rounded-full">
                    {m.date}
                  </span>
                )}
              </div>
            )) : <div className="text-xs text-[#948b7f] italic py-4 text-center">No medication data available</div>}
          </div>
        )}

        {/* ── Allergies ── */}
        {activeTab === "Allergies" && (
          <div className="flex flex-wrap gap-2">
            {(data.allergies || []).length > 0 ? data.allergies.map((a, i) => (
              <div key={i} className="bg-[#fff0f0] text-[#c53030] border border-[#f0bcbc] rounded-md px-3 py-1.5 text-xs font-semibold">
                {a.allergen}{a.reaction ? ` — ${a.reaction}` : ''}
              </div>
            )) : <div className="text-xs text-[#948b7f] italic py-4 text-center">No allergy data available</div>}
          </div>
        )}

        {/* ── Problems ── */}
        {activeTab === "Problems" && (
          <div>
            {(data.problems || []).length > 0 ? data.problems.map((p, i) => (
              <ProblemItem key={i} text={p} />
            )) : <div className="text-xs text-[#948b7f] italic py-4 text-center">No active problems</div>}
          </div>
        )}

        {/* ── Social ── */}
        {activeTab === "Social" && (
          <div className="space-y-3 text-xs">
            {['tobacco', 'alcohol', 'exercise', 'drugs'].map(key => {
              const val = data.social?.[key];
              if (!val || val === 'Unknown') return null;
              return (
                <div key={key} className="bg-[#faf8f5] p-2 rounded border border-[#e4ded5]">
                  <span className="block text-[9px] text-[#948b7f] uppercase font-semibold">{key}</span>
                  <span className="text-[#5c5549]">{val}</span>
                </div>
              );
            })}
            {(!data.social || Object.values(data.social).every(v => !v || v === 'Unknown')) && (
              <div className="text-xs text-[#948b7f] italic py-4 text-center">No social history available</div>
            )}
          </div>
        )}

        {/* ── Family ── */}
        {activeTab === "Family" && (
          <div className="space-y-2 text-xs">
            {data.family && Object.keys(data.family).length > 0 ? (
              Object.entries(data.family).map(([rel, cond]) => (
                <div key={rel} className="flex justify-between items-start py-1.5 border-b border-[#f5f3ef] last:border-0">
                  <span className="font-semibold capitalize text-[#948b7f] shrink-0 mr-2">{rel}:</span>
                  <span className="text-[#5c5549] text-right">{cond}</span>
                </div>
              ))
            ) : <div className="text-xs text-[#948b7f] italic py-4 text-center">No family history available</div>}
          </div>
        )}

        {/* ── Surgical ── */}
        {activeTab === "Surgical" && (
          <div>
            {(data.surgical || []).length > 0 ? (
              <ul className="list-disc pl-4 text-xs text-[#5c5549] space-y-1.5">
                {data.surgical.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            ) : <div className="text-xs text-[#948b7f] italic py-4 text-center">No surgical history available</div>}
          </div>
        )}
      </div>
    </div>
  );
};

const ClinicalEncounterDashboard = ({ rawData, fallbackPatient, onClose }) => {
  const data = useMemo(() => {
    const parsed = parseAthenaSummary(rawData);
    // Same-day / not-yet-documented encounters come back without a
    // demographics block, which rendered "Unknown Patient" even though the
    // chart the modal was opened from knows exactly who this is (pt 13810,
    // 09/02). Only fill the gaps — real summary demographics still win.
    if (fallbackPatient?.name && (!parsed.patient?.name || /^unknown/i.test(parsed.patient.name))) {
      const name = fallbackPatient.name;
      parsed.patient = {
        ...parsed.patient,
        name,
        initials: name.split(/[\s,]+/).filter(Boolean).map((w) => w[0]).join('').toUpperCase().slice(0, 2) || '??',
        id: parsed.patient?.id || fallbackPatient.id || '',
        dob: parsed.patient?.dob || fallbackPatient.dob || '',
      };
    }
    return parsed;
  }, [rawData, fallbackPatient]);

  return (
    <div className="bg-[#f0ede8] flex flex-col font-sans text-[#1a1613]" style={{ height: '100%', minHeight: 0 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono&display=swap');
        .font-sans { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
        .font-mono { font-family: 'DM Mono', monospace; }
        .custom-scrollbar::-webkit-scrollbar { width: 5px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #d1c9bc; border-radius: 10px; }
      `}</style>
      
      {/* Top Bar */}
      <div className="bg-white border-b border-[#e4ded5] p-4 flex items-center gap-6 shrink-0 relative">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#e8653a] to-[#c24e26]" />
        
        <div className="w-12 h-12 rounded-full bg-[#e8653a] text-white flex items-center justify-center font-bold text-lg shadow-inner">
          {data.patient.initials}
        </div>
        
        <div className="flex-1">
          <h1 className="text-xl font-bold tracking-tight text-[#1a1613]">{data.patient.name}</h1>
          <div className="flex gap-3 text-[11px] font-medium text-[#948b7f] mt-0.5">
            {data.patient.id && <><span>ID #{data.patient.id}</span><span className="text-[#d1c9bc]">·</span></>}
            {data.patient.dob && <><span>DOB {data.patient.dob}</span><span className="text-[#d1c9bc]">·</span></>}
            <span>{data.patient.age} {data.patient.gender}</span>
            {data.patient.appt && <><span className="text-[#d1c9bc]">·</span><span>Encounter: {data.patient.appt}</span></>}
          </div>
        </div>

        <div className="flex gap-2 flex-wrap justify-end">
          {Object.entries(data.vitals).filter(([, val]) => val && val !== '--' && val !== '--/--').map(([key, val]) => (
            <div key={key} className="bg-[#faf8f5] border border-[#e4ded5] rounded-full px-3 py-1 text-[11px] font-semibold text-[#5c5549]">
              <span className="uppercase text-[9px] text-[#948b7f] mr-1">{key}</span> {val}
            </div>
          ))}
        </div>

        {onClose && (
          <button onClick={onClose} className="p-2 hover:bg-[#f5f3ef] rounded-full transition-colors text-[#948b7f]">
            <CloseIcon />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-hidden" style={{ display: 'grid', gridTemplateColumns: '280px 1fr 320px', minHeight: 0 }}>
        {/* Column 1: CC & HPI */}
        <div className="overflow-y-auto p-4 bg-white border-r border-[#e4ded5] custom-scrollbar" style={{ minHeight: 0 }}>
          {data.cc.length > 0 && (
            <section className="mb-3">
              <h2 className="text-[10px] uppercase font-bold tracking-widest text-[#e8653a] mb-1">Chief Complaint</h2>
              <p className="text-[12px] font-normal text-[#3a342c] leading-snug">{data.cc.join(', ')}</p>
            </section>
          )}

          {data.hpiLines.length > 0 && (
            <section>
              <h2 className="text-[10px] uppercase font-bold tracking-widest text-[#e8653a] mb-2">HPI</h2>
              <div className="space-y-2">
                {data.hpiLines.map((line, i) => {
                  const skipLabel = ['History of Present Illness', 'HPI'].includes(line.label);
                  return (
                  <div key={i} className="text-[12px] leading-relaxed text-[#5c5549]">
                    {!skipLabel && (
                      <span className={`font-bold text-[#1a1613] ${line.flag ? 'bg-[#fff3cd] px-1 rounded' : ''}`}>
                        {line.label} —{' '}
                      </span>
                    )}
                    {line.text}
                  </div>
                  );
                })}
              </div>
            </section>
          )}

          {data.cc.length === 0 && data.hpiLines.length === 0 && (
            <div className="text-sm text-[#948b7f] italic py-8 text-center">No chief complaint or HPI recorded</div>
          )}
        </div>

        {/* Column 2: Assessment & Plan */}
        <div className="overflow-y-auto p-6 custom-scrollbar flex flex-col text-left" style={{ minHeight: 0, background: '#f7f5f1' }}>
          <h2 className="text-[10px] uppercase font-bold tracking-widest text-[#e8653a] mb-4">Assessment & Plan</h2>
          
          <div className="flex-1">
            {data.dx.length > 0 ? data.dx.map((item, i) => <DxCard key={i} item={item} />) : data.apText ? (
              <div>
                {data.apText.split('\n').filter(l => l.trim()).map((line, i) => {
                  const trimmed = line.replace(/^\s*-\s*/, '');
                  const icdMatch = trimmed.match(/\(([A-Z]\d{2,}(?:\.\d+)?)\)/);
                  const textWithoutIcd = icdMatch ? trimmed.replace(icdMatch[0], '').trim() : trimmed;
                  return (
                    <div key={i} className="py-1.5 flex items-start gap-2">
                      <span className="text-[#c4bdb3] mt-[3px] shrink-0">•</span>
                      <span className="text-[13px] font-normal text-[#3a342c] leading-snug">
                        {textWithoutIcd}
                        {icdMatch && (
                          <span className="ml-2 text-[9px] font-mono bg-[#f0ede8] text-[#948b7f] px-1.5 py-0.5 rounded-full inline-block align-middle">
                            {icdMatch[1]}
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-[#948b7f] italic py-8 text-center">No assessment & plan recorded</div>
            )}
          </div>

          {(data.followup.instructions !== 'None provided.' || data.followup.returnAppt !== 'Not specified') && (
            <div className="mt-6 bg-white border border-[#e4ded5] rounded-xl p-4 flex items-center justify-between shadow-sm">
              {data.followup.instructions !== 'None provided.' && (
                <div className="bg-[#fff8e7] border border-[#edd07a] px-4 py-2 rounded-lg text-xs font-medium text-[#b8860b] flex-1 mr-4">
                  {data.followup.instructions}
                </div>
              )}
              {data.followup.returnAppt !== 'Not specified' && (
                <div className="text-right shrink-0">
                  <span className="block text-[10px] uppercase font-bold text-[#1b8c5a]">Return Visit</span>
                  <span className="font-bold text-sm text-[#1a1613]">{data.followup.returnAppt}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Column 3: Reference History */}
        <div className="p-4 bg-[#faf8f5] border-l border-[#e4ded5] flex flex-col" style={{ minHeight: 0 }}>
          <RefTabs data={data} />
        </div>
      </div>
    </div>
  );
};

export default ClinicalEncounterDashboard;
